// src/services/NotificationService.js
import apiClient from '../api/client';
import { useToastStore } from '../components/UI/Toast';

/**
 * Služba pro správu oznámení a upozornění
 */
export class NotificationService {
    /**
     * Získá nastavení notifikací
     * @returns {Promise<object>} - Nastavení notifikací
     */
    static async getSettings() {
        try {
            const { data } = await apiClient.get('/api/notifications/settings');
            return data;
        } catch (error) {
            console.error('Failed to get notification settings:', error);
            throw error;
        }
    }

    /**
     * Aktualizuje nastavení notifikací
     * @param {object} settings - Nové nastavení
     * @returns {Promise<object>} - Aktualizované nastavení
     */
    static async updateSettings(settings) {
        try {
            const { data } = await apiClient.put('/api/notifications/settings', settings);
            useToastStore.getState().addToast('Nastavení notifikací bylo uloženo', 'success');
            return data;
        } catch (error) {
            useToastStore.getState().addToast('Chyba při ukládání nastavení notifikací', 'danger');
            throw error;
        }
    }

    /**
     * Odešle testovací notifikaci
     * @param {string} channel - Kanál notifikace (email, sms, slack, ...)
     * @returns {Promise<object>} - Výsledek testu
     */
    static async testNotification(channel) {
        try {
            const { data } = await apiClient.post('/api/notifications/test', { channel });
            useToastStore.getState().addToast(`Testovací notifikace odeslána na kanál ${channel}`, 'success');
            return data;
        } catch (error) {
            useToastStore.getState().addToast(`Chyba při odesílání testovací notifikace na kanál ${channel}`, 'danger');
            throw error;
        }
    }

    /**
     * Získá nepřečtená oznámení
     * @param {number} limit - Maximální počet oznámení
     * @returns {Promise<Array>} - Seznam nepřečtených oznámení
     */
    static async getUnreadNotifications(limit = 10) {
        try {
            const { data } = await apiClient.get('/api/notifications/unread', { params: { limit } });
            return data;
        } catch (error) {
            console.error('Failed to get unread notifications:', error);
            throw error;
        }
    }

    /**
     * Označí oznámení jako přečtené
     * @param {string} notificationId - ID oznámení
     * @returns {Promise<object>} - Výsledek operace
     */
    static async markAsRead(notificationId) {
        try {
            const { data } = await apiClient.post(`/api/notifications/mark-read/${notificationId}`);
            return data;
        } catch (error) {
            console.error('Failed to mark notification as read:', error);
            throw error;
        }
    }

    /**
     * Označí všechna oznámení jako přečtená
     * @returns {Promise<object>} - Výsledek operace
     */
    static async markAllAsRead() {
        try {
            const { data } = await apiClient.post('/api/notifications/mark-all-read');
            useToastStore.getState().addToast('Všechna oznámení byla označena jako přečtená', 'success');
            return data;
        } catch (error) {
            useToastStore.getState().addToast('Chyba při označování oznámení jako přečtených', 'danger');
            throw error;
        }
    }

    /**
     * Odstraní oznámení
     * @param {string} notificationId - ID oznámení
     * @returns {Promise<object>} - Výsledek operace
     */
    static async deleteNotification(notificationId) {
        try {
            const { data } = await apiClient.delete(`/api/notifications/${notificationId}`);
            return data;
        } catch (error) {
            console.error('Failed to delete notification:', error);
            throw error;
        }
    }

    /**
     * Získá historii oznámení
     * @param {object} filters - Filtry pro historii
     * @param {number} page - Číslo stránky
     * @param {number} perPage - Počet položek na stránku
     * @returns {Promise<object>} - Historie oznámení
     */
    static async getHistory(filters = {}, page = 1, perPage = 20) {
        try {
            const { data } = await apiClient.get('/api/notifications/history', {
                params: {
                    ...filters,
                    page,
                    perPage
                }
            });
            return data;
        } catch (error) {
            console.error('Failed to get notification history:', error);
            throw error;
        }
    }

    /**
     * Získá počet nepřečtených oznámení
     * @returns {Promise<number>} - Počet nepřečtených oznámení
     */
    static async getUnreadCount() {
        try {
            const { data } = await apiClient.get('/api/notifications/unread-count');
            return data.count;
        } catch (error) {
            console.error('Failed to get unread notifications count:', error);
            return 0;
        }
    }

    /**
     * Nastaví callback pro příjem notifikací v reálném čase
     * @param {Function} callback - Callback funkce pro příjem notifikací
     * @returns {Function} - Funkce pro zrušení callbacku
     */
    static subscribeToRealTimeNotifications(callback) {
        // V reálné implementaci by zde byla WebSocket/SSE logika
        // Toto je jen zástupná implementace

        console.log('Subscribing to real-time notifications');

        // Interval pro simulaci příchozích notifikací (pouze pro účely vývoje)
        const intervalId = setInterval(() => {
            // Zde by byla logika pro příjem notifikací v reálném čase

            // Pro účely vývoje můžeme zavolat callback s fiktivními daty
            if (process.env.NODE_ENV === 'development' && Math.random() < 0.1) {
                callback({
                    id: `notification-${Date.now()}`,
                    type: ['uptime', 'security', 'update'][Math.floor(Math.random() * 3)],
                    message: 'Nová testovací notifikace',
                    createdAt: new Date().toISOString(),
                    read: false
                });
            }
        }, 30000);

        // Vracíme funkci pro zrušení callbacku
        return () => {
            clearInterval(intervalId);
            console.log('Unsubscribing from real-time notifications');
        };
    }
}

export default NotificationService;